import { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { DataContext } from "../../providers/DataProvider";
import InputField from "../form_inputs/InputField";
import MarketingOptIn from "../form_inputs/MarketingOptIn";
import SubmitButton from "../form_inputs/SubmitButton";

import ShowSucessMessage from "../modal/ShowSucessMessage";

import { renderFormField } from "../form_inputs/RenderFormField";

function DynamicForm() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");

  // State declarations
  const [formState, setFormState] = useState({});
  const [status, setStatus] = useState("");
  const [triggerError, setTriggerError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  //Form Validation States
  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    job_title: "",
    organisation: "",
    email: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Hooks
  const location = useLocation();
  const { formid } = useParams();
  const { markFormObject, handlePopulateEachMarkForm, populateEachMarkForm } =
    useContext(DataContext);

  // Effects
  useEffect(() => {
    handlePopulateEachMarkForm(formid);
  }, [formid, handlePopulateEachMarkForm]);

  useEffect(() => {
    if (status === "success" || status === "error") {
      showSuccessMessage();
    }
  }, [status]);

  // Form handling functions
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormState({
      ...formState,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error for this field if the form has been submitted once
    // and the user starts typing in it again
    if (formSubmitted) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    setFormChanged(true);
  };

  const validateField = (name, value, fieldType, isRequired) => {
    switch (name) {
      case "first_name":
        return !value || value.trim() === "" ? "First name is required" : "";
      case "last_name":
        return !value || value.trim() === "" ? "Last name is required" : "";
      case "job_title":
        return !value || value.trim() === "" ? "Job title is required" : "";
      case "organisation":
        return !value || value.trim() === "" ? "Organisation is required" : "";
      case "email":
        if (!value || value.trim() === "") return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? "Invalid email address" : "";
      default:
        if (isRequired && (!value || value.trim() === "")) {
          return `This field is required`;
        }
        return "";
    }
  };

  const formValidation = () => {
    const newErrors = {
      first_name: validateField("first_name", formState.first_name),
      last_name: validateField("last_name", formState.last_name),
      job_title: validateField("job_title", formState.job_title),
      organisation: validateField("organisation", formState.organisation),
      email: validateField("email", formState.email),
    };

    if (populateEachMarkForm.formFields) {
      populateEachMarkForm.formFields.forEach((field) => {
        const fieldId = field.label.toLowerCase().replace(/\s+/g, "_");

        newErrors[fieldId] = validateField(
          fieldId,
          formState[fieldId],
          field.type,
          field.required
        );
      });
    }

    // Update the errors state
    setErrors(newErrors);

    // Return true if there are any errors
    return Object.values(newErrors).some((error) => error !== "");
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    // Mark the form as submitted to show all error messages
    setFormSubmitted(true);

    // Validate form fields
    const hasErrors = formValidation();

    if (hasErrors) {
      // Find the first field with an error and focus it
      const firstErrorField = Object.keys(errors).find(
        (key) => validateField(key, formState[key]) !== ""
      );

      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
      }

      return;
    }

    setLoading(true);
    setStatus("sending");
    console.log("Location:", location.pathname.split("/")[1]);

    try {
      console.log("Form data:", formState);
      console.log(
        "Form ID:",
        formid,
        "Form title:",
        populateEachMarkForm.title
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASEURL}/form/${formid}/submit`,
        {
          ...formState,
          signup_location: populateEachMarkForm.title,
          show_on_slack: populateEachMarkForm.formFields.filter(
            (field) => field.show_on_slack === true
          ),
        }
      );

      console.log("Response:", response.data);

      if (response.data) {
        console.log("Submission successful:", response.data);
        setLoading(false);
        setFormState({});
        setFormChanged(false);
        setTriggerError(false);
        setStatus("success");

        if (redirectTo) {
          setTimeout(() => {
            const parentWindow = window.parent;
            parentWindow.location.href = redirectTo;
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
      setTriggerError(true);
      setStatus("error");
      return;
    }

    setStatus("success");
  };

  // Utility functions
  const showSuccessMessage = () => {
    if (status === "success" && !showSuccess) {
      setShowSuccess(true);
      setLoading(true);
      setTimeout(() => {
        resetForm();
        setLoading(false);
        setShowSuccess(false);
      }, 3000);
    }

    if (status === "error" && triggerError) {
      setTimeout(() => {
        setLoading(false);
        setTriggerError(false);
      }, 3000);
    }
  };

  const resetForm = () => {
    setFormState({});
    setErrors({
      first_name: "",
      last_name: "",
      job_title: "",
      organisation: "",
      email: "",
    });

    setFormChanged(false);
    setFormSubmitted(false);
    setStatus("");
    setTriggerError(false);
    setLoading(false);
  };

  // Loading state
  if (!populateEachMarkForm) {
    return <div>Loading form...</div>;
  }

  // Render
  return (
    <div className=" ">
      <div className="">
        {/* Success and Error Messages */}
        {status === "success" && showSuccess && <ShowSucessMessage />}
        {triggerError && status === "error" && (
          <div
            className="bg-red-100 text-red-700 p-4 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 shadow-lg"
            role="alert"
          >
            <p className="font-bold">Error</p>
            <p>There was an error submitting your form. Please try again</p>
          </div>
        )}

        {/* Form */}
        <form
          className={`flex flex-col gap-3 w-full p-4 ${
            loading ? "opacity-50 border shadow cursor-not-allowed" : ""
          }`}
          onSubmit={handleFormSubmit}
          onChange={() => {
            setFormChanged(true);
          }}
        >
          <p className="text-gray-600 text-sm">All fields are mandatory.</p>

          {/* Name Fields */}
          <div className="flex justify-stretch gap-3">
            <div className="flex flex-col w-full">
              <InputField
                id="first_name"
                label="First name"
                value={formState.first_name || ""}
                onChange={handleInputChange}
                disabled={loading}
                required
              />
              {formSubmitted && errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
            </div>

            <div className="flex flex-col w-full">
              <InputField
                id="last_name"
                label="Last Name"
                value={formState.last_name || ""}
                onChange={handleInputChange}
                disabled={loading}
                required
              />
              {formSubmitted && errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Professional Information */}
          <div className="flex flex-col">
            <InputField
              id="job_title"
              label="Job Title"
              value={formState.job_title || ""}
              onChange={handleInputChange}
              disabled={loading}
              required
            />
            {formSubmitted && errors.job_title && (
              <p className="text-red-500 text-xs mt-1">{errors.job_title}</p>
            )}
          </div>
          <div className="flex flex-col">
            <InputField
              id="organisation"
              label="Organisation"
              value={formState.organisation || ""}
              onChange={handleInputChange}
              disabled={loading}
              required
            />
            {formSubmitted && errors.organisation && (
              <p className="text-red-500 text-xs mt-1">{errors.organisation}</p>
            )}
          </div>

          {/* Contact Information */}
          <div className="flex flex-col">
            <InputField
              id="email"
              label="Email"
              type="email"
              value={formState.email || ""}
              onChange={handleInputChange}
              disabled={loading}
              required
            />
            {formSubmitted && errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Dynamic Form Fields */}
          {populateEachMarkForm.formFields &&
            populateEachMarkForm.formFields
              .sort((a, b) => a.order - b.order)
              .map((field) =>
                renderFormField(
                  field,
                  formState,
                  handleInputChange,
                  loading,
                  formSubmitted,
                  errors
                )
              )}

          {/* Disclaimer Text */}
          {populateEachMarkForm.disclaimerText && (
            <p className="text-sm text-gray-600 mt-2 mb-4">
              {populateEachMarkForm.disclaimerText}
            </p>
          )}

          {/* Marketing Opt-in */}
          <div className="flex flex-col">
            <MarketingOptIn
              id="accepts_marketing"
              checked={formState.accepts_marketing || false}
              onChange={(event) =>
                setFormState({
                  ...formState,
                  accepts_marketing: event.target.checked,
                })
              }
              disabled={loading}
            />
          </div>

          {/* Submit Button */}
          <SubmitButton
            onClick={handleFormSubmit}
            disabled={loading || !formChanged}
            loading={loading}
          />
        </form>
      </div>
    </div>
  );
}

export default DynamicForm;
